import { useEffect, useRef, useState, type ReactNode } from "react";
import { PLACEHOLDERS } from "@/utils/constants/appConstants/commonConstants";

interface Props {
    onHide: () => void;
    handleSearch?: (searchText: string) => void;
    reset?: () => void;
    title: string;
    id: string;
    documentSearchEnabled?: boolean;
    leadingContent?: ReactNode;
    trailingContent?: ReactNode;
    /** Current applied search; close only resets when this is non-empty. */
    activeSearch?: string | null;
}

const DOCUMENT_SEARCH_TITLES = new Set(["Envelopes", "Templates", "Forms"]);

const SearchBar = (props: Props) => {
    const {
        onHide,
        handleSearch,
        title,
        documentSearchEnabled,
        leadingContent,
        trailingContent,
        activeSearch,
    } = props;
    const [searchText, setSearchText] = useState(activeSearch ?? "");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleClearSearch = () => {
        onHide();
        setSearchText("");
        // Only clear/reload when a search is actually applied — closing an empty bar should not refetch
        if (activeSearch?.trim()) {
            handleSearch?.("");
        }
    };

    const [prevTitle, setPrevTitle] = useState(title);

    useEffect(() => {
        if (title !== prevTitle) {
            handleClearSearch();
        }
        setPrevTitle(title);
    }, [title]);

    const submitSearch = () => {
        handleSearch?.(searchText.trim());
    };

    const placeholder =
        documentSearchEnabled && DOCUMENT_SEARCH_TITLES.has(title)
            ? PLACEHOLDERS.DOCUMENT_SEARCH
            : PLACEHOLDERS.SEARCH;

    return (
        <div className="gs-search-bar">
            {leadingContent ? <div className="gs-search-bar__leading">{leadingContent}</div> : null}
            <div className="gs-search-bar__inner">
                <button
                    type="button"
                    id={`${props.id}-cross-icon`}
                    className="gs-search-bar__close"
                    onClick={handleClearSearch}
                    aria-label="Close search"
                >
                    <em className="icon ni ni-cross" />
                </button>
                <input
                    type="text"
                    id={`${props.id}-search-input`}
                    ref={inputRef}
                    value={searchText}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            submitSearch();
                        }
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="gs-search-bar__input"
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    id={`${props.id}-search-input-icon`}
                    className="gs-search-bar__submit"
                    onClick={submitSearch}
                    aria-label="Search"
                >
                    <em className="icon ni ni-search" />
                </button>
            </div>
            {trailingContent ? <div className="gs-search-bar__trailing">{trailingContent}</div> : null}
        </div>
    );
};

export default SearchBar;
